"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssignmentMetaProvider = void 0;
const lodash_1 = require("lodash");
const operators_1 = require("rxjs/operators");
const BaseDataProvider_1 = require("./BaseDataProvider");
class AssignmentMetaProvider extends BaseDataProvider_1.BaseDataProvider {
    constructor(refs, connectivityService, settingsProvider) {
        super(refs, connectivityService);
        this.query = (options) => {
            let query = this.refs.assignmentMetaCollection();
            query = (0, lodash_1.isNil)(options.ownerId) ? query : query.where('ownerId', '==', options.ownerId);
            query = (0, lodash_1.isNil)(options.groupId) ? query : query.where('groupId', '==', options.groupId);
            query = (0, lodash_1.isNil)(options.module) ? query : query.where('module', '==', options.module);
            query = (0, lodash_1.isNil)(options.status) ? query : query.where('status', '==', options.status);
            query = (0, lodash_1.isNil)(options.type) ? query : query.where('type', '==', options.type);
            query = (0, lodash_1.isNil)(options.isActive) ? query : query.where('summary.isActive', '==', options.isActive);
            query = (0, lodash_1.isNil)(options.teacherId) ? query : query.where(`usersWithAccess`, 'array-contains', options.teacherId);
            query = (0, lodash_1.isNil)(options.lessonId) ? query : query.where('lessonId', '==', options.lessonId);
            query = (0, lodash_1.isNil)(options.createdBefore) ? query : query.where('createdAt', '<=', options.createdBefore.getTime());
            query = (0, lodash_1.isNil)(options.createdAfter)
                ? query
                : query.where('createdAt', '>=', options.createdAfter.getTime()).orderBy('createdAt', 'desc');
            query = (0, lodash_1.isNil)(options.teacherActivityBefore)
                ? query
                : query.where('teacherActivityAt', '<=', options.teacherActivityBefore.getTime());
            query = (0, lodash_1.isNil)(options.sortByOrder) ? query : query.orderBy('order', options.sortByOrder);
            query = (0, lodash_1.isNil)(options.orderBy) ? query : query.orderBy(options.orderBy, 'desc');
            query = (0, lodash_1.isNil)(options.limit) ? query : query.limit(options.limit);
            query = (0, lodash_1.isNil)(options.passedStartTime) ? query : query.where('scheduled.startDate', '<', options.passedStartTime);
            query = (0, lodash_1.isNil)(options.started) ? query : query.where('scheduled.started', '==', options.started);
            query = (0, lodash_1.isNil)(options.passedCloseTime) ? query : query.where('scheduled.closeDate', '<', options.passedCloseTime);
            query = (0, lodash_1.isNil)(options.closed) ? query : query.where('scheduled.closed', '==', options.closed);
            query = (0, lodash_1.isNil)(options.types) ? query : query.where('type', 'in', options.types);
            //TEMP FOR MIGRATION
            query = (0, lodash_1.isNil)(options.createdBeforeDescending)
                ? query
                : query.where('createdAt', '<', options.createdBeforeDescending.getTime()).orderBy('createdAt', 'desc');
            return query;
        };
        this.settingsProvider = settingsProvider;
    }
    latestForOwnerByStatusStream(status, type, ownerId) {
        return this.findStream({ ownerId, status, type, orderBy: 'createdAt', limit: 1 }).pipe((0, operators_1.map)((results) => (0, lodash_1.first)(results)));
    }
    meta(id) {
        return this.docPromise(this.refs.assignmentMetaDocument(id));
    }
    metaStream(id) {
        return this.docDataStream(this.refs.assignmentMetaDocument(id), false);
    }
    metasByGroup(groupId, createdAfter) {
        return this.find({ groupId, createdAfter });
    }
    metasByModule(module, createdAfter) {
        return this.find({ module, createdAfter, sortByOrder: 'asc' });
    }
    archivedMetasByModule(moduleId) {
        const query = this.refs.assignmentMetaArchiveCollection();
        query.where('module', '==', moduleId);
        return this.collectionPromise(query);
    }
    archivedMetasByGroup(groupId) {
        const query = this.refs.assignmentMetaArchiveCollection();
        query.where('groupId', '==', groupId);
        return this.collectionPromise(query);
    }
    metasByGroupForTeacherStream(groupId, teacherId) {
        return this.findStream({
            groupId,
            teacherId,
        });
    }
    archivedMetasByGroupForTeacherStream(groupId, teacherId) {
        return this.findArchiveStream({
            groupId,
            teacherId,
        });
    }
    metasForTeacherStream(teacherId, limit) {
        return this.findStream({
            teacherId,
            limit,
            orderBy: 'createdAt',
        });
    }
    metasByGroupForTeacher(groupId, teacherId) {
        return this.find({
            groupId,
            teacherId,
        });
    }
    metasByLesson(lessonId, teacherId) {
        return this.find({ lessonId, teacherId });
    }
    metasByLessonStream(lessonId, teacherId) {
        return this.findStream({ lessonId, teacherId });
    }
    metasForOwnerByStatus(status, type, ownerId) {
        return this.find({ ownerId, status, type });
    }
    async metaWithSettings(id) {
        const [meta, settings] = await Promise.all([this.meta(id), this.settingsProvider.settings(id)]);
        return meta && settings && { meta, settings };
    }
    async settingsForMetas(metas) {
        const results = await Promise.all(metas.map(async (meta) => {
            const settings = await this.settingsProvider.settings(meta._id);
            return settings && { meta, settings };
        }));
        return (0, lodash_1.compact)(results);
    }
    homeworkByGroup(groupId, status, createdAfter) {
        return this.find({ groupId, status, type: 'homework', createdAfter });
    }
    lastTestForGroup(groupId, createdAfter) {
        return this.find({ groupId, type: 'test', createdAfter, limit: 1 });
    }
    openRealtimeWithoutActivity(teacherActivityBefore, limit) {
        return this.find({
            type: 'realtime',
            teacherActivityBefore,
            status: 'open',
            limit,
        });
    }
    /** Oct 2021: This query was slow without an index.
     * Changed Query from being ordered on the server with a limit of 1 into client side sorting.
     * This query should return few results so we client side sort it and picked the most recent one. */
    latestActiveRealtimeForOwnerStream(ownerId) {
        return this.findStream({
            ownerId,
            status: 'open',
            type: 'realtime',
        }).pipe((0, operators_1.map)((results) => (0, lodash_1.sortBy)(results, (result) => result.createdAt)), (0, operators_1.map)((results) => (0, lodash_1.last)(results)));
    }
    latestActiveTestForOwnerStream(ownerId) {
        return this.findStream({
            ownerId,
            orderBy: 'createdAt',
            type: 'test',
            isActive: true,
            limit: 1,
        }).pipe((0, operators_1.map)((results) => results[0]));
    }
    /** tries to find any meta for the given lesson and group where the teacher is the owner or has been shared with */
    async latestMetaByLessonInGroup(lessonId, groupId, teacherId, type) {
        const metas = await this.find({
            type,
            groupId,
            lessonId,
            teacherId,
        });
        return (0, lodash_1.last)((0, lodash_1.sortBy)(metas, (meta) => meta.createdAt));
    }
    latestTestsForLessonStream(lessonId, teacherId, limit) {
        return this.findStream({
            orderBy: 'createdAt',
            limit,
            lessonId,
            teacherId,
            type: 'test',
        });
    }
    latestAssignmentMetaForLessonAndTypeStream(lessonId, teacherId, limit, type) {
        return this.findStream({
            orderBy: 'createdAt',
            limit,
            lessonId,
            teacherId,
            type,
        });
    }
    async migratedMetasBetween(type, from, to) {
        return this.find({
            type,
            createdAfter: from,
            createdBefore: to,
        });
    }
    async allMetasWithAccessForUser(userId) {
        return this.find({ teacherId: userId });
    }
    find(options) {
        return this.collectionPromise(this.query(options));
    }
    findStream(options) {
        return this.collectionDataStream(this.query(options), false);
    }
    findArchiveStream(options) {
        return this.collectionDataStream(this.archiveAssignmentMetaQuery(options), false);
    }
    async assignmentsToStart() {
        return this.find({
            passedStartTime: Date.now(),
            started: false,
        });
    }
    async assignmentsToClose() {
        return this.find({
            passedCloseTime: Date.now(),
            closed: false,
        });
    }
    async hightestOrderForModule(module) {
        const meta = await this.find({
            module,
            sortByOrder: 'desc',
            limit: 1,
        });
        return meta[0]?.order;
    }
    async nextAssignmentBatchForMigration(createdBefore, limit) {
        return this.find({
            types: ['homework', 'test'],
            createdBeforeDescending: createdBefore,
            limit,
        });
    }
    async hasSharedAssignmentBefore(groupId) {
        const meta = await this.find({ groupId, types: ['homework', 'test'], limit: 1 });
        return !!meta.length;
    }
    archiveAssignmentMetaQuery(options) {
        let query = this.refs.assignmentMetaArchiveCollection();
        query = (0, lodash_1.isNil)(options.groupId) ? query : query.where('groupId', '==', options.groupId);
        query = (0, lodash_1.isNil)(options.teacherId) ? query : query.where(`usersWithAccess`, 'array-contains', options.teacherId);
        return query;
    }
}
exports.AssignmentMetaProvider = AssignmentMetaProvider;
//# sourceMappingURL=AssignmentMetaProvider.js.map